import React, { useState, useEffect } from "react";
import {
  Tooltip,
  IconButton,
  ClickAwayListener,
  Popper,
  Paper,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { FaQuestionCircle } from "react-icons/fa";
import axios from "axios";

interface StatusTooltipProps {
  status: string;
}

interface TooltipData {
  tooltip_text: string;
  next_status: string | null;
}

const StatusTooltip: React.FC<StatusTooltipProps> = ({ status }) => {
  const [tooltips, setTooltips] = useState<Record<string, TooltipData>>({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const fetchTooltips = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_DOMAIN_URL}api/gift-certificate/get-status-tooltips`,
          { withCredentials: true }
        );

        if (response.data.status === "success") {
          setTooltips(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching status tooltips:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTooltips();
  }, []);

  if (loading || !tooltips[status]) {
    return null;
  }

  const tooltipData = tooltips[status];

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isMobile) {
      setAnchorEl(event.currentTarget);
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setAnchorEl(null);
  };

  const tooltipContent = (
    <div className="max-w-xs">
      <div className="font-medium mb-1">{tooltipData.tooltip_text}</div>
      {tooltipData.next_status && (
        <div className="text-sm opacity-90">
          (Next Status: {tooltipData.next_status})
        </div>
      )}
    </div>
  );

  // Mobile version with custom Popper
  if (isMobile) {
    return (
      <ClickAwayListener onClickAway={handleClose}>
        <div style={{ display: "inline-block" }}>
          <IconButton
            size="small"
            className="ml-1"
            onClick={handleClick}
            sx={{
              padding: "4px",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <FaQuestionCircle
              size={12}
              className="text-gray-400 hover:text-gray-600"
            />
          </IconButton>

          <Popper
            open={open}
            anchorEl={anchorEl}
            placement="top"
            sx={{ zIndex: 1300 }}
          >
            <Paper
              elevation={8}
              sx={{
                p: 2,
                maxWidth: 300,
                backgroundColor: "rgba(0, 0, 0, 0.9)",
                color: "white",
                borderRadius: 1,
                fontSize: "0.875rem",
                boxShadow: theme.shadows[8],
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, mb: 0.5, color: "white" }}
              >
                {tooltipData.tooltip_text}
              </Typography>
              {tooltipData.next_status && (
                <Typography
                  variant="caption"
                  sx={{ opacity: 0.9, color: "white" }}
                >
                  (Next Status: {tooltipData.next_status})
                </Typography>
              )}
            </Paper>
          </Popper>
        </div>
      </ClickAwayListener>
    );
  }

  // Desktop version with regular Tooltip
  return (
    <Tooltip
      title={tooltipContent}
      arrow
      placement="top"
      enterDelay={300}
      leaveDelay={200}
    >
      <IconButton size="small" className="ml-1">
        <FaQuestionCircle
          size={12}
          className="text-gray-400 hover:text-gray-600"
        />
      </IconButton>
    </Tooltip>
  );
};

export default StatusTooltip;
